import React, { useState } from 'react';
import './index.scss';

type SceneItem = { title: string; description: string; image: string };

const scene1 = '/images/aip/Procurement-solution2/scene-demand-planning.png';
const scene2 = '/images/aip/Procurement-solution2/scene-sourcing-pricing.png';
const scene3 = '/images/aip/Procurement-solution2/scene-contract-agreement.png';
const scene4 = '/images/aip/Procurement-solution2/scene-self-service-mall.png';
const scene5 = '/images/aip/Procurement-solution2/scene-order-collaboration.png';
const scene6 = '/images/aip/Procurement-solution2/scene-supplier-lifecycle.png';
const scene7 = '/images/aip/Procurement-solution2/scene-material-wash.png';
const scene8 = '/images/aip/Procurement-solution2/scene-risk-analytics.png';
const mode1 = '/images/aip/Procurement-solution2/mode-productive-procurement.png';
const mode2 = '/images/aip/Procurement-solution2/mode-mro-procurement.png';
const panorama = '/images/aip/Procurement-solution2/digital-procurement-panorama.png';
const flowImage = '/images/aip/Procurement-solution2/process-flow.png';
const case1 = '/images/aip/Procurement-solution2/energy-case.jpg';
const case2 = '/images/aip/Procurement-solution2/chemical-case.jpg';
const case3 = '/images/aip/Procurement-solution2/factory-case.jpg';

const topScenes: SceneItem[] = [
  { title: '需求与计划管理', description: '提前理清“买什么、何时买、谁来买”，减少临时加急与重复采购。', image: scene1 },
  { title: '寻源与定价管理', description: 'AI高效寻源，自动化采购策略引擎，提升采购透明度与议价效率。', image: scene2 },
  { title: '合同与协议管理', description: '合同线上管理与变更留痕，降低执行争议，提升协同效率。', image: scene3 },
  { title: '自助式采购商城', description: '电商化采购体验与一站式选品，支持业务部门自助高效下单。', image: scene4 }
];

const bottomScenes: SceneItem[] = [
  { title: '订单协同与履约', description: '订单状态实时同步，异常更早发现，减少跨部门追单成本。', image: scene5 },
  { title: '供应商全生命周期管理', description: 'AI顾问提供供应商健康度分析，持续优化供应商质量与稳定交付能力。', image: scene6 },
  { title: '洗车式物料管理', description: '在保持原有流程基础上做数据清洗与持续优化，降低改造冲击。', image: scene7 },
  { title: '风险管控与数据分析', description: '将风险管理前置，用数据驱动持续优化，不再被动补救。', image: scene8 }
];

function SceneTabs({ items, defaultIndex = 0 }: { items: SceneItem[]; defaultIndex?: number }) {
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  const active = items[activeIndex] || items[0];
  return (
    <div className="scene-pair">
      <div className="scene-nav">
        {items.map((item, index) => (
          <article
            key={item.title}
            className={`scene-nav-item ${index === activeIndex ? 'is-active' : ''}`}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => setActiveIndex(index)}
          >
            <h3>{item.title}</h3>
            <p>{item.description}</p>
          </article>
        ))}
      </div>
      <div className="scene-viewer">
        <img src={active.image} alt={`${active.title}示意图`} loading="lazy" />
      </div>
    </div>
  );
}

export default function Procurement() {
  return (
    <div className="procurement-page">
      <main>
        <section className="hero section">
          <div className="container hero-grid">
            <div className="hero-content">
              <h1>数智化采购解决方案</h1>
              <p>
                面向中大型企业，端点科技提供一套能落地、能扩展、能看见结果的采购升级方案：流程更快、价格更优、风险更可控，管理层能实时掌握经营变化。
              </p>
            </div>
            <aside className="hero-card" aria-label="方案价值摘要">
              <div className="hero-kpi">
                <div>
                  <b>7+</b>
                  <span>核心采购场景统一管理</span>
                </div>
                <div>
                  <b>8 大</b>
                  <span>采购升级能力模块</span>
                </div>
                <div>
                  <b>多行业</b>
                  <span>头部企业落地验证</span>
                </div>
                <div>
                  <b>AI 驱动</b>
                  <span>辅助决策与风险预警</span>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="section section-alt" id="scenes">
          <div className="container">
            <div className="section-head">
              <h2>全链路一体化</h2>
              <p>让每一次采购都有迹可循，统一采购流程模板与审批规则，将繁杂业务沉淀为标准底座，实现全流程线上化、可视化管理。</p>
            </div>

            <div className="panorama-merged-media-only">
              <img className="panorama-main-image" src={panorama} alt="数智化采购业务全景图" loading="lazy" />
            </div>

            <div className="scene-showcase">
              <SceneTabs items={topScenes} />
              <SceneTabs items={bottomScenes} />
            </div>
          </div>
        </section>

        <section className="section section-alt" id="modes">
          <div className="container">
            <div className="section-head">
              <h2>双模式灵活适配</h2>
              <p>兼顾刚性控本与敏捷响应</p>
            </div>
            <div className="modes-grid">
              <article className="modes-card">
                <figure className="modes-card-thumb">
                  <img src={mode1} alt="生产性采购配图" loading="lazy" />
                </figure>
                <div className="modes-card-body">
                  <h3>生产性采购</h3>
                  <p>针对原材料与关键零部件，强化协议采购与供应商协同，深度压降成本，确保供应链韧性。</p>
                </div>
              </article>
              <article className="modes-card">
                <figure className="modes-card-thumb">
                  <img src={mode2} alt="非生产性采购配图" loading="lazy" />
                </figure>
                <div className="modes-card-body">
                  <h3>非生产性采购（MRO）</h3>
                  <p>引入“网购式”商城体验，员工按需下单，系统自动合规。</p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="section" id="ai-agent">
          <div className="container">
            <div className="section-head">
              <h2>AI+数智化赋能</h2>
              <p>让采购决策跑在风险发生之前</p>
            </div>
            <div className="ai-board">
              <img src={flowImage} alt="AI+数智化赋能采购流程示意图" loading="lazy" />
            </div>
          </div>
        </section>

        <section className="section" id="cases">
          <div className="container">
            <div className="section-head">
              <h2>行业实践证明</h2>
              <p>采购升级可以又快又稳</p>
            </div>
            <div className="case-grid">
              <a className="case-link" href="https://terminus.io/case-detail/16082" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <div className="case-media-placeholder">
                    <img src={case1} alt="山东能源集团案例配图" />
                  </div>
                  <h3>山东能源集团｜大型能源集团协同采购平台</h3>
                  <p className="case-one-line">
                    协同采购平台助力山东能源实现数字化采购，打造智慧化工厂；全流程协同，全链路预警，实现供应商全生命周期闭环管理。
                  </p>
                </article>
              </a>
              <a className="case-link" href="https://terminus.io/case-detail/2007001" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <div className="case-media-placeholder">
                    <img src={case2} alt="京博集团案例配图" />
                  </div>
                  <h3>京博集团｜大型制造企业智慧供应链平台</h3>
                  <p className="case-one-line">以需求计划、采购计划管理为切入，构建大计划管理平台；上下游全链协同，提升采购管控能力。</p>
                </article>
              </a>
              <a className="case-link" href="https://terminus.io/case-detail/2011003" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <div className="case-media-placeholder">
                    <img src={case3} alt="中国宏桥案例配图" />
                  </div>
                  <h3>中国宏桥｜特大型多产业融合企业数字采购平台</h3>
                  <p className="case-one-line">
                    提高询价、招投标的透明度，规范化招标流程；实现商城目录化、自助化采购；重塑供应商全生命周期管理体系。
                  </p>
                </article>
              </a>
            </div>
          </div>
        </section>

        <section className="section section-alt" id="delivery">
          <div className="container">
            <div className="section-head">
              <h2>落地流程保障</h2>
              <p>从架构可行性、实施节奏、组织协同到运营复盘，确保项目不仅上线，而且持续产生业务价值。</p>
            </div>
            <div className="process-flow">
              <article className="process-step">
                <span className="process-num">01</span>
                <h3>现状诊断</h3>
                <p>梳理组织、流程、系统和数据现状，明确目标蓝图与优先场景。</p>
              </article>
              <article className="process-step">
                <span className="process-num">02</span>
                <h3>方案设计</h3>
                <p>输出业务架构、流程规则、集成边界与分阶段里程碑，确保范围清晰。</p>
              </article>
              <article className="process-step">
                <span className="process-num">03</span>
                <h3>平台建设</h3>
                <p>按场景推进配置与集成，建立标准模板和运营机制，保障上线质量。</p>
              </article>
              <article className="process-step">
                <span className="process-num">04</span>
                <h3>运营优化</h3>
                <p>通过数据分析与 AI 预警持续优化策略，实现价值复盘与能力进化。</p>
              </article>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

